import { ArrowRight, Lock, TrendingUp, X } from "lucide-react";

interface PlanNotAvailableProps {
  isOpen: boolean;
  onClose: () => void;
  requiredPlan?: string;
  currentPlan?: string;
  mode?: "customUniverse" | "stock" | "restrictedTab";
}

const PlanNotAvailable = ({
  isOpen,
  onClose,
  requiredPlan,
  currentPlan,
  mode = "stock",
}: PlanNotAvailableProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm bg-black/40 p-4">
      <div className="bg-white dark:bg-gray-900/90 rounded-xl shadow-2xl w-full max-w-lg animate-in">
        {/* Content */}
        <div className="p-8 relative">
          <button
            onClick={onClose}
            className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 dark:hover:text-white p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Icon */}
          <div className="relative flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full blur-xl opacity-50 animate-pulse"></div>
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-2xl">
                <Lock className="w-10 h-10 text-white" />
              </div>
            </div>
          </div>

          {/* Heading */}
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent">
              You&apos;re almost there!
            </h3>
            <div className="w-16 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400 mx-auto rounded-full mb-4"></div>
          </div>

          {/* Message Box */}
          <div className="mb-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 dark:from-amber-400/30 dark:via-orange-400/30 dark:to-amber-400/20 rounded-xl"></div>
            <div className="absolute inset-0 bg-white/40 dark:bg-black/20 backdrop-blur-sm rounded-xl"></div>

            <div className="relative p-6 border-2 border-amber-200/50 dark:border-amber-700/50 rounded-xl text-center space-y-3">
              {mode === "restrictedTab" ? (
                <>
                  <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed max-w-md mx-auto pt-2">
                    This Universe falls outside your current Plan.
                  </p>
                  <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed max-w-md mx-auto">
                    Upgrade to {""}
                    <span className="font-bold text-blue-600 dark:text-blue-400">
                      {requiredPlan}
                    </span>
                    .
                  </p>
                  {currentPlan && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      Your current plan:{" "}
                      <span className="font-semibold">{currentPlan}</span>
                    </p>
                  )}
                </>
              ) : mode === "customUniverse" ? (
                <>
                  <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed max-w-md mx-auto pt-2">
                    Upgrade to{" "}
                    <span className="font-bold text-blue-600 dark:text-blue-400">
                      {requiredPlan}
                    </span>{" "}
                    for this feature.
                  </p>
                  {currentPlan && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      Your current plan:{" "}
                      <span className="font-semibold">{currentPlan}</span>
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed max-w-md mx-auto pt-2">
                    This stock falls outside your current Plan.
                  </p>
                  <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed max-w-md mx-auto">
                    Upgrade to {""}
                    <span className="font-bold text-blue-600 dark:text-blue-400">
                      {process.env.NEXT_PUBLIC_REQUIRED_PLAN}
                    </span>
                    .
                  </p>
                  {currentPlan && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      Your current plan:{" "}
                      <span className="font-semibold">{currentPlan}</span>
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 mb-4">
            <button
              onClick={() => (window.location.href = "/billing")}
              className="cursor-pointer group flex-1 relative overflow-hidden px-3 py-2 bg-[#00A85F] dark:bg-gradient-to-t dark:from-[#066766] dark:to-[#0FA243] text-white font-semibold rounded-xl shadow-2xl transition-all duration-300 hover:opacity-90 hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              <TrendingUp className="w-5 h-5 relative z-10" />
              <span className="relative z-10">Upgrade</span>
              <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
            </button>

            <button
              onClick={onClose}
              className="cursor-pointer px-3 py-2 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-xl border-2 border-gray-200 dark:border-gray-600 transition-all"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanNotAvailable;
